// TourContext.tsx
import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { useProfile } from "../hooks/use-profile";
import { useApiMutation } from "../hooks/use-api-mutation";

type TourName =
  | "orders"
  | "overview"
  | "buyerOrderDetails"
  | "merchantOrderDetails";
interface TourContextType {
  isTourActive: Record<TourName, boolean>;
  startTour: (tour: TourName) => void;
  finishTour: (tour: TourName) => void;
  isLoading: boolean;
}

const TourContext = createContext<TourContextType | undefined>(undefined);

export const TourProvider = ({ children }: { children: ReactNode }) => {
  const [isLoading, setIsLoading] = useState(false);

  // Only fetch the profile once the user is authenticated. TourProvider wraps
  // the whole app (including the public landing page), so an unguarded
  // useProfile() would fire GET /user/profile with no token and 401 on landing.
  const hasAuthToken =
    typeof window !== "undefined" && !!localStorage.getItem("token");
  const profileQuery = useProfile(hasAuthToken);

  const updateMutation = useApiMutation<any>({
    endpoint: "/user/coach-marks",
    method: "PATCH",
    meta: { silent: true },
  });
  // Define the initial state for tours
  const [isTourActive, setIsTourActive] = useState<Record<TourName, boolean>>({
    overview: true,
    orders: true,
    buyerOrderDetails: true,
    merchantOrderDetails: true,
  });

  // This state mirrors the backend state to avoid unnecessary API calls
  const [serverTourState, setServerTourState] = useState<
    Record<TourName, boolean>
  >({
    overview: false,
    orders: false,
    buyerOrderDetails: false,
    merchantOrderDetails: false,
  });

  //Function to update the tour profile in the backend
  const updateTourProfile = async (state: Record<TourName, boolean>) => {
    try {
      setIsLoading(true);
      const data = await updateMutation.mutateAsync({
        data: {
          hasSeenOverviewTour: state.overview,
          hasSeenOrdersTour: state.orders,
          hasSeenBuyerOrderDetailsTour: state.buyerOrderDetails,
          hasSeenMerchantOrderDetailsTour: state.merchantOrderDetails,
        },
      });
      return data?.data;
    } catch (error) {
      console.error("Error updating tour profile:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Derive tour state from the shared profile cache.
  useEffect(() => {
    const data = profileQuery.data?.data;
    if (data) {
      const newTourState = {
        overview: data.hasSeenOverviewTour || false,
        orders: data.hasSeenOrdersTour || false,
        buyerOrderDetails: data.hasSeenBuyerOrderDetailsTour || false,
        merchantOrderDetails: data.hasSeenMerchantOrderDetailsTour || false,
      };
      setIsTourActive(newTourState);
      setServerTourState(newTourState);
    }
  }, [profileQuery.data]);

  const startTour = (tour: TourName) => {
    // When starting a tour, we set its state to `false` (meaning it's active for viewing)
    const newState = { ...isTourActive, [tour]: false };
    setIsTourActive(newState);
  };

  const finishTour = async (tour: TourName) => {
    // If already true in backend, skip API call but update local state
    if (serverTourState[tour] === true) {
      setIsTourActive((prev) => ({ ...prev, [tour]: true }));
      return;
    }

    const updatedState = { ...isTourActive, [tour]: true };
    setIsTourActive(updatedState);
    try {
      await updateTourProfile(updatedState);
      setServerTourState({ ...serverTourState, [tour]: true }); // sync local mirror of backend
    } catch (error) {
      console.error(`Failed to finish tour ${tour}:`, error);
    }
  };

  return (
    <TourContext.Provider
      value={{
        isTourActive,
        startTour,
        finishTour,
        isLoading: isLoading || profileQuery.isLoading,
      }}>
      {children}
    </TourContext.Provider>
  );
};

export const useTour = () => {
  const context = useContext(TourContext);
  if (context === undefined) {
    throw new Error("useTour must be used within a TourProvider");
  }
  return context;
};
